import React, { useRef, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface AutoCarouselProps {
  children: React.ReactNode[];
  autoPlaySpeed?: number;
  itemsPerView?: number;
}

export const AutoCarousel: React.FC<AutoCarouselProps> = ({ 
  children, 
  autoPlaySpeed = 3000,
  itemsPerView = 3 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Responsive itemsPerView for mobile
  const [responsiveItemsPerView, setResponsiveItemsPerView] = useState(itemsPerView);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) setContainerWidth(containerRef.current.clientWidth);
      if (window.innerWidth < 640) {
        setResponsiveItemsPerView(1);
      } else if (window.innerWidth < 900) {
        setResponsiveItemsPerView(2);
      } else {
        setResponsiveItemsPerView(itemsPerView || 3);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [itemsPerView]);

  const totalItems = children.length;
  const maxIndex = Math.max(0, totalItems - responsiveItemsPerView);

  // Ensure current index stays valid when items per view or total items change
  useEffect(() => {
    if (currentIndex > maxIndex) {
      setCurrentIndex(0);
    }
  }, [responsiveItemsPerView, totalItems, maxIndex]);

  useEffect(() => {
    if (isPaused || totalItems <= responsiveItemsPerView) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        const next = prev + 1;
        return next > maxIndex ? 0 : next;
      });
    }, autoPlaySpeed);

    return () => clearInterval(interval);
  }, [isPaused, autoPlaySpeed, maxIndex, totalItems, responsiveItemsPerView]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? maxIndex : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  if (totalItems === 0) return null;

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="overflow-hidden" ref={containerRef}>
        <div 
          ref={trackRef}
          className="flex gap-5 transition-transform duration-700 ease-in-out"
          style={{
            transform: (() => {
              if (!containerRef.current || !trackRef.current) return `translateX(0px)`;
              const gap = parseFloat(getComputedStyle(trackRef.current).columnGap || getComputedStyle(trackRef.current).gap || '0');
              const itemWidth = containerRef.current.clientWidth / responsiveItemsPerView;
              const translatePx = currentIndex * (itemWidth + gap);
              return `translateX(-${translatePx}px)`;
            })(),
          }}
        >
          {children.map((child, index) => (
            <div 
              key={index} 
              className="flex-shrink-0"
              style={{ width: containerWidth ? `${containerWidth / responsiveItemsPerView}px` : `${100 / responsiveItemsPerView}%`, boxSizing: 'border-box' }}
            >
              {child}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation buttons */}
      {totalItems > responsiveItemsPerView && (
        <>
          <button
            onClick={handlePrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 p-3 bg-white hover:bg-blue-50 rounded-full shadow-xl border border-gray-200 hover:border-blue-300 transition-all duration-300 hover:scale-110 group"
            aria-label="Previous"
          >
            <ChevronLeft className="h-5 w-5 text-gray-700 group-hover:text-blue-600" />
          </button>
          <button
            onClick={handleNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 p-3 bg-white hover:bg-blue-50 rounded-full shadow-xl border border-gray-200 hover:border-blue-300 transition-all duration-300 hover:scale-110 group"
            aria-label="Next"
          >
            <ChevronRight className="h-5 w-5 text-gray-700 group-hover:text-blue-600" />
          </button>
        </>
      )}

      {/* Dots indicator */}
      {totalItems > responsiveItemsPerView && (
        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: maxIndex + 1 }).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'w-8 bg-blue-600' 
                  : 'w-2 bg-gray-300 hover:bg-blue-400'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
